import { debounce } from "lodash";
import { useCallback, useState } from "react";
import { testURL } from "../../../lib/TestURL";
import { testAccountName } from "../../../lib/testAccountName";
import useBaseURL from "../../../config/useBaseUrl.js";
import { defaultServerUrl } from "../../../config/constants/urls.js";

export const useValidateAccount = () => {
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isValidAccount, setIsValidAccount] = useState(undefined);
  const [checkAccountErrorMSG, setCheckAccountErrorMSG] = useState(null);
  const [isLoadingCheckAccount, setIsLoadingCheckAccount] = useState(false);
  const baseURL = useBaseURL();

  const validateAccount = useCallback(
    debounce(async (account, serverUrl) => {
      if (!account) {
        setIsValidAccount(false);
        setIsDuplicate(false);
        setCheckAccountErrorMSG("Database name is required");
        return;
      }

      if (!testAccountName(account)) {
        setIsValidAccount(false);
        setIsDuplicate(false);
        setCheckAccountErrorMSG("Invalid database name format");
        return;
      }

      setIsValidAccount(true);
      setIsLoadingCheckAccount(true);

      const url = `${baseURL}/api/account/account_exists`;
      const requestData = { account_name: account, server_url: serverUrl };

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        });

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const data = await response.json();
        const isDuplicateAccount = data;

        setIsDuplicate(isDuplicateAccount);
        setCheckAccountErrorMSG(
          isDuplicateAccount ? "Database already exists" : null,
        );
      } catch (error) {
        setIsDuplicate(false);
        setCheckAccountErrorMSG(error.message || "An error occurred");
      } finally {
        setIsLoadingCheckAccount(false);
      }
    }, 1000), // Adjust the debounce delay as needed
    [baseURL],
  );

  return {
    validateAccount,
    isDuplicate,
    isValidAccount,
    checkAccountErrorMSG,
    isLoadingCheckAccount,
  };
};
export const useValidateIDBG = () => {
  const [isValid, setIsValid] = useState(undefined);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const baseURL = useBaseURL();

  const validateIDBG = useCallback(
    async (idbg, serverUrl) => {
      if (!idbg) {
        setIsValid(false);
        setErrorMessage("Account name is required");
        return;
      }
      if (idbg.length < 3) {
        setIsValid(false);
        setErrorMessage("Account name is less than minimum length");
        return;
      }
      if (idbg.length > 50) {
        setIsValid(false);
        setErrorMessage("Account name exceeds maximum length");
        return;
      }

      const url = `${baseURL}/api/inerygui/guiname_exists`;
      const requestData = { guiname: idbg, server_url: serverUrl };

      setIsLoading(true);

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        });

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const data = await response.json();
        const isDuplicate = data === true;
        const validationStatus = !isDuplicate;
        setIsValid(validationStatus);
        setErrorMessage(isDuplicate ? "Account name already exists" : null);
      } catch (error) {
        setIsValid(false);
        setErrorMessage(error.message || "An error occurred");
      } finally {
        setIsLoading(false);
      }
    }, // Debounce delay of 300ms
    [baseURL], // Dependencies
  );

  return {
    validateIDBG,
    isIDBGValid: isValid,
    idbgErrMSG: errorMessage,
    isIDBGLoading: isLoading,
  };
};

export const useValidateServer = () => {
  const [isServerValid, setIsServerValid] = useState(undefined);
  const [validateServerErrorMSG, setValidateServerErrorMSG] = useState(null);
  const [isLoadingValidateServer, setIsLoadingValidateServer] = useState(false);
  const baseURL = useBaseURL();

  const validateServer = useCallback(
    async (serverURL) => {
      if (serverURL === defaultServerUrl) {
        setIsServerValid(true);
        return;
      }

      if (!serverURL) {
        setIsServerValid(false);
        setValidateServerErrorMSG("Server URL is required");
        return;
      }

      if (!testURL(serverURL)) {
        setIsServerValid(false);
        setValidateServerErrorMSG("Invalid URL format");
        return;
      }

      const url = `${baseURL}/api/inerygui/server_valid`;
      const requestData = { server_url: serverURL };

      setIsLoadingValidateServer(true);

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        });

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const validationStatus = await response.json();

        setIsServerValid(validationStatus);
        setValidateServerErrorMSG(
          validationStatus ? null : "Server is not valid",
        );
      } catch (error) {
        setIsServerValid(false);
        setValidateServerErrorMSG(
          error.message || "An error occurred during validation",
        );
      } finally {
        setIsLoadingValidateServer(false);
      }
    },
    [baseURL],
  );

  const resetState = useCallback(() => {
    setIsServerValid(undefined);
    setValidateServerErrorMSG(null);
  }, [setIsServerValid, setValidateServerErrorMSG]);

  return {
    validateServer,
    resetState,
    isServerValid,
    validateServerErrorMSG,
    isLoadingValidateServer,
  };
};

export const useHandleFormSubmit = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const baseURL = useBaseURL();

  const handleFormSubmit = async (formData) => {
    const url = `${baseURL}/api/inerygui/register`;

    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();
      setData(data);
    } catch (error) {
      console.log(error);
      setError(error.message || "An error occurred during registration");
    } finally {
      setIsLoading(false);
    }
  };

  const resetStates = () => {
    setData(null);
    setError(null);
    setIsLoading(false);
  };

  return {
    handleFormSubmit,
    formDataResponse: data,
    formResponseError: error,
    isFormResponseLoading: isLoading,
    resetFormState: resetStates,
  };
};
